import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { AttendanceAPI } from '../../api/attendanceAPI';
import AttendanceClassDateSelector from './AttendanceClassDateSelector';
import { periodsAPI } from '../../api/periodsAPI';
import { getTodayLocal, getCurrentTimezoneConfig } from '../../utils/timezoneUtils';
import { calculateSessionStatistics } from '../../utils/attendanceCalculations';
import { useSchoolTimezone } from '../../hooks/useSchoolTimezone';
import AttendanceStatusEditor from './AttendanceStatusEditor';
import AddStudentToReportModal from './AddStudentToReportModal';
import RemoveStudentModal from './RemoveStudentModal';
import { updateAttendanceRecord, addStudentToSession, removeStudentFromSession, getClassroomStudents, getUserAccessibleClassrooms } from '../../api/attendanceReportsAPI';

export default function AttendanceReportsPage() {
  console.log('🚀 AttendanceReportsPage: Component rendering started');
  console.log('🔍 Component mount timestamp:', new Date().toISOString());
  
  const { getAccessTokenSilently } = useAuth0();
  const { currentSchool, currentOrganization, isLoading: tenantLoading } = useTenant();
  const { displayPeriodTime } = useSchoolTimezone();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  console.log('🔍 AttendanceReportsPage: Initial state - currentSchool:', currentSchool?.id, 'tenantLoading:', tenantLoading);
  console.log('🔍 AttendanceReportsPage: URL params:', searchParams.toString());
  console.log('🔍 getAccessTokenSilently function available:', typeof getAccessTokenSilently);
  
  // Filter states
  const [selectedClass, setSelectedClass] = useState(() => {
    // Check URL params first, then localStorage, then default to empty
    const urlClass = searchParams.get('class');
    if (urlClass) return urlClass;
    
    const savedClass = localStorage.getItem('attendanceReports_lastSelectedClass');
    return savedClass || '';
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    // Check URL params first, then default to today (user's device date)
    const urlDate = searchParams.get('date');
    const todayFormatted = getTodayLocal();
    
    // If there's a URL date, use it (including 'all'); otherwise use today
    // 'all' is a special value to indicate showing all dates
    return urlDate || todayFormatted;
  });
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [selectedReportId, setSelectedReportId] = useState(() => {
    // Check URL params for specific report
    return searchParams.get('reportId') || null;
  });
  
  // Data states
  const [attendanceReports, setAttendanceReports] = useState([]);
  const [reportStudents, setReportStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingReportsForClass, setLoadingReportsForClass] = useState(false);
  const [error, setError] = useState(null);
  
  // Server-side pagination state for reports
  const [serverPagination, setServerPagination] = useState({
    currentPage: 0,
    pageSize: 5, // Match client-side default
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  
  // Filter options
  const [classes, setClasses] = useState([]);
  const [classesLoaded, setClassesLoaded] = useState(false);
  const [periods, setPeriods] = useState([]);
  
  // Search state
  const [search, setSearch] = useState('');
  
  // Client-side pagination state for the displayed reports (keeping for backward compatibility)
  const [currentPage, setCurrentPage] = useState(0);
  const reportsPerPage = 5; // Reduced from 7 to 5 for better UX
  
  // Student pagination state
  const [currentStudentPage, setCurrentStudentPage] = useState(0);
  const studentsPerPage = 10;

  // Periods scroll state
  const [periodScrollPosition, setPeriodScrollPosition] = useState(0);
  const periodsContainerRef = useRef(null);

  // Editing state variables
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [studentToRemove, setStudentToRemove] = useState(null);
  const [availableStudentsForAdd, setAvailableStudentsForAdd] = useState([]);
  const [loadingAvailableStudents, setLoadingAvailableStudents] = useState(false);
  const [isRemovingStudent, setIsRemovingStudent] = useState(false);

  // RBAC: User's accessible classrooms
  const [accessibleClassrooms, setAccessibleClassrooms] = useState({
    accessType: 'all', // 'all' or 'restricted'
    classroomIds: []
  });
  const hasAttemptedFetch = useRef(false);

  // Load classes that have reports for the selected date (separate from pagination)
  const loadClassesWithReports = useCallback(async () => {
    if (!currentSchool?.id) {
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      // Get the effective date for filtering
      const urlDate = searchParams.get('date');
      const effectiveDate = selectedDate || urlDate || getTodayLocal();
      
      // Create filters for ALL reports for this date (no pagination)
      const filters = {};
      if (effectiveDate && effectiveDate !== 'all') {
        filters.date = effectiveDate;
      }
      
      // Get ALL reports for the date without pagination limits
      const result = await AttendanceAPI.getSessionsBySchoolFiltered(
        currentSchool.id, 
        filters, 
        { page: 0, pageSize: 1000 }, // Large page size to get all reports
        token
      );
      
      const allReportsForDate = Array.isArray(result) ? result : result.data || [];
      
      // Extract unique classes from ALL reports for this date
      const uniqueClasses = [...new Map(
        allReportsForDate
          .filter(r => r.classId && r.className)
          .map(r => [r.classId, { id: r.classId, name: r.className }])
      ).values()];
      
      setClasses(uniqueClasses);
      setClassesLoaded(true);
      
    } catch (err) {
      console.error('Error loading classes with reports:', err);
      // Fallback to empty array
      setClasses([]);
      setClassesLoaded(false);
    }
  }, [currentSchool?.id, selectedDate, searchParams, getAccessTokenSilently]);

  // Fetch user's accessible classrooms on mount
  useEffect(() => {
    const fetchAccessibleClassrooms = async () => {
      try {
        hasAttemptedFetch.current = true;
        const token = await getAccessTokenSilently();
        const data = await getUserAccessibleClassrooms(token);
        setAccessibleClassrooms(data);
      } catch (err) {
        console.error('Error loading accessible classrooms:', err);
        // Default to 'all' access on error to avoid blocking UI
        setAccessibleClassrooms({ accessType: 'all', classroomIds: [] });
      }
    };

    // Fetch immediately unless tenant is explicitly loading or we've already attempted
    if (tenantLoading !== true && !hasAttemptedFetch.current) {
      fetchAccessibleClassrooms();
    }
  }, [tenantLoading, getAccessTokenSilently]);

  // Handle initial loading state based on tenant context
  useEffect(() => {
    if (!tenantLoading && !currentSchool?.id) {
      setLoading(false);
    }
  }, [tenantLoading, currentSchool?.id]);

  // Save selected class to localStorage whenever it changes
  useEffect(() => {
    if (selectedClass) {
      localStorage.setItem('attendanceReports_lastSelectedClass', selectedClass);
    } else {
      // Remove from localStorage when class is cleared
      localStorage.removeItem('attendanceReports_lastSelectedClass');
      console.log('🗑️ Removed saved class from localStorage');
    }
  }, [selectedClass]);

  // Handle class change loading state
  useEffect(() => {
    if (selectedClass && attendanceReports.length > 0) {
      console.log('🔄 Class changed, setting loading state for reports filtering');
      setLoadingReportsForClass(true);
      
      // Small delay to allow filtering to complete
      const timer = setTimeout(() => {
        setLoadingReportsForClass(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [selectedClass, attendanceReports.length]);

  // Load classes with reports when component mounts or date changes
  useEffect(() => {
    // Reset the loaded flag when date changes so we reload classes
    setClassesLoaded(false);
  }, [selectedDate, searchParams]);

  // Load classes when not loaded
  useEffect(() => {
    if (!classesLoaded) {
      loadClassesWithReports();
    }
  }, [classesLoaded, loadClassesWithReports]);

  // Filter reports by selected date and class (from AttendanceClassDateSelector only)
  // Since we're using server-side pagination, this will now work with the current page of data
  const reportsForSelectedDate = useMemo(() => {
    const urlDate = searchParams.get('date');
    const showAllDates = urlDate === 'all';
    
    const filtered = attendanceReports.filter(report => {
      // Filter by date if selected and not showing all dates
      if (selectedDate && !showAllDates) {
        const reportDateSimple = report.date.includes('T') ? report.date.split('T')[0] : report.date;
        if (reportDateSimple !== selectedDate) {
          return false;
        }
      }
      
      // Filter by class if selected
      if (selectedClass && report.classId !== parseInt(selectedClass)) {
        console.log('🔍 Class filter mismatch:', {
          selectedClass: selectedClass,
          selectedClassType: typeof selectedClass,
          reportClassId: report.classId,
          reportClassIdType: typeof report.classId,
          equal: report.classId === selectedClass,
          strictEqual: report.classId === selectedClass
        });
        return false;
      }
      
      return true;
    });
    
    console.log('Reports filtered for selected date/class:', {
      selectedDate,
      selectedClass,
      showAllDates,
      totalReports: attendanceReports.length,
      filteredReports: filtered.length,
      filtered: filtered.map(r => ({ id: r.id, className: r.className, period: r.period, periodId: r.periodId }))
    });
    
    return filtered;
  }, [attendanceReports, selectedDate, selectedClass, searchParams]);

  // For server-side pagination, we use the reports directly since filtering is done on the server
  const paginatedReports = useMemo(() => {
    // When using server-side pagination (which we always do now), just return the reports
    // The server has already filtered and paginated them
    return attendanceReports;
  }, [attendanceReports]);

  // Calculate total pages using server pagination info
  const totalPages = serverPagination.totalPages;
  const totalReports = serverPagination.totalCount;

  // Student filtering and pagination (computed after reportStudents is set)
  const filteredStudents = useMemo(() => {
    return reportStudents.filter(student => {
      return `${student.first} ${student.last}`.toLowerCase().includes(search.toLowerCase()) ||
             (student.email && student.email.toLowerCase().includes(search.toLowerCase()));
    });
  }, [reportStudents, search]);

  // Paginated students
  const paginatedStudents = useMemo(() => {
    const startIndex = currentStudentPage * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  }, [filteredStudents, currentStudentPage, studentsPerPage]);

  // Calculate total student pages
  const totalStudentPages = Math.ceil(filteredStudents.length / studentsPerPage);

  // Reset student page when filtered students change
  useEffect(() => {
    setCurrentStudentPage(0);
  }, [filteredStudents]);

  // Auto-select first report whenever the reports list changes
  useEffect(() => {
    const urlReportId = searchParams.get('reportId');
    
    if (reportsForSelectedDate.length > 0) {
      // If URL specifies a report and it exists in the current list, use that
      if (urlReportId && reportsForSelectedDate.find(r => r.id === urlReportId)) {
        console.log('URL report found - selecting:', urlReportId);
        setSelectedReportId(urlReportId);
      } else {
        // Auto-select first report immediately when reports list changes
        console.log('Auto-selecting first report from updated list:', reportsForSelectedDate[0]);
        setSelectedReportId(reportsForSelectedDate[0].id);
      }
    } else {
      // Clear selection when no reports available
      console.log('No reports available - clearing selection');
      setSelectedReportId(null);
    }
  }, [reportsForSelectedDate, searchParams]);

  // Load attendance reports with pagination
  const loadAttendanceReports = useCallback(async (page = 0, pageSize = 5) => {
    if (!currentSchool?.id) {
      console.log('❌ No currentSchool.id available, setting loading to false');
      setLoading(false);
      return;
    }
    
    console.log('✅ Loading attendance reports for school:', currentSchool.id, 'page:', page, 'pageSize:', pageSize);
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessTokenSilently();
      
      // Build filters from URL parameters and component state
      const filters = {};
      const urlClass = searchParams.get('class');
      const urlPeriod = searchParams.get('period');
      const urlDate = searchParams.get('date');
      
      // Use selected values if available, otherwise fall back to URL params
      if (selectedClass || urlClass) filters.classId = selectedClass || urlClass;
      if (urlPeriod) filters.periodId = urlPeriod;  
      
      // Always include date filter - either from selected date or URL, with fallback to today
      const effectiveDate = selectedDate || urlDate || getTodayLocal();
      if (effectiveDate && effectiveDate !== 'all') {
        filters.date = effectiveDate;
      }
      
      const pagination = { page, pageSize };
      
      console.log('🔍 Making API call with filters:', filters, 'pagination:', pagination);
      
      // Always use filtered API since we typically have date filters
      const result = await AttendanceAPI.getSessionsBySchoolFiltered(currentSchool.id, filters, pagination, token);
      
      // Handle both old format (array) and new format (object with data/pagination)
      const reports = Array.isArray(result) ? result : result.data || [];
      const paginationInfo = result.pagination || {
        currentPage: 0,
        pageSize: reports.length,
        totalCount: reports.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      };
      
      // Process and format the reports with frontend statistics calculation
      const formattedReports = reports.map(report => {
        // Calculate statistics from raw records using utility function
        const stats = calculateSessionStatistics(report.records);
        
        return {
          id: report.id,
          className: report.className || 'Unknown Class',
          classId: report.classId,
          subject: report.subject || 'Unknown Subject',
          subjectCode: report.subjectCode,
          instructor: report.instructor || 'Unknown Instructor',
          instructorId: report.instructorId,
          instructorEmail: report.instructorEmail,
          date: report.date,
          timestamp: report.timestamp,
          period: report.period || `Period ${Math.floor(Math.random() * 3) + 1}`,
          periodId: report.periodId,
          status: report.status || 'COMPLETED',
          ...stats, // Spread the calculated statistics
          created_at: report.created_at,
          updatedAt: report.updatedAt,
          wasUpdated: report.wasUpdated,
          records: report.records // Keep records for future use if needed
        };
      });

      setAttendanceReports(formattedReports);
      setServerPagination(paginationInfo);
      
      console.log('📋 Attendance reports loaded:', {
        count: formattedReports.length,
        pagination: paginationInfo,
        classroomIds: formattedReports.map(r => ({
          reportId: r.id,
          className: r.className,
          classId: r.classId,
          classIdType: typeof r.classId
        }))
      });
      
    } catch (error) {
      console.error('❌ Error loading attendance reports:', error);
      setError(error.message || 'Failed to load attendance reports');
      setAttendanceReports([]);
      setServerPagination({
        currentPage: 0,
        pageSize: 5,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    } finally {
      setLoading(false);
    }
  }, [currentSchool?.id, selectedClass, selectedDate, searchParams, getAccessTokenSilently]);

  // Load attendance reports
  useEffect(() => {
    console.log('� LoadAttendanceReports useEffect triggered - currentSchool?.id:', currentSchool?.id);
    loadAttendanceReports(0, 5); // Load first page with 5 reports
  }, [currentSchool?.id, loadAttendanceReports]);

  // Reload when filters change and reset to first page
  useEffect(() => {
    if (currentSchool?.id) {
      console.log('🔄 Filters changed, reloading reports from first page');
      setCurrentPage(0); // Reset to first page
      loadAttendanceReports(0, 5); // Load first page with new filters
    }
  }, [selectedClass, selectedDate, searchParams, currentSchool?.id, loadAttendanceReports]);

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    console.log('📄 Page change requested:', newPage);
    setCurrentPage(newPage);
    
    // Always reload data since we're using server-side pagination
    loadAttendanceReports(newPage, serverPagination.pageSize);
  };

  // Handle periods scroll
  const scrollPeriods = (direction) => {
    if (!periodsContainerRef.current) return;
    
    const container = periodsContainerRef.current;
    const scrollAmount = 200; // Pixels to scroll
    const newPosition = direction === 'left' 
      ? Math.max(0, periodScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, periodScrollPosition + scrollAmount);
    
    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setPeriodScrollPosition(newPosition);
  };

  // Check if periods need scrolling and scroll state
  const periodsNeedScrolling = periods.length > 6; // Approximate threshold
  const canScrollLeft = periodScrollPosition > 0;
  const canScrollRight = periodsContainerRef.current ? 
    periodScrollPosition < (periodsContainerRef.current.scrollWidth - periodsContainerRef.current.clientWidth) : 
    false;

  // Load periods based on selection
  useEffect(() => {
    const loadPeriods = async () => {
      if (!currentSchool?.id) {
        setPeriods([]);
        return;
      }

      // Determine which class to use for period loading
      let classIdForPeriods = null;
      
      // Priority 1: If a specific report is selected, use its class
      if (selectedReportId) {
        const selectedReportData = attendanceReports.find(r => r.id === selectedReportId);
        if (selectedReportData) {
          classIdForPeriods = selectedReportData.classId;
          console.log('Loading periods for selected report class:', selectedReportData.className, classIdForPeriods);
        }
      }
      
      // Priority 2: If a class is selected in the selector, use that class
      if (!classIdForPeriods && selectedClass) {
        classIdForPeriods = parseInt(selectedClass);
        console.log('Loading periods for selected class:', selectedClass);
      }

      if (classIdForPeriods) {
        // Load periods specifically for the determined class
        try {
          const token = await getAccessTokenSilently();
          const classroomPeriods = await periodsAPI.getPeriodsByClassroom(classIdForPeriods, token);
          
          // Format periods for the UI
          const formattedPeriods = classroomPeriods.map(period => ({
            id: period.id,
            name: period.name || `Period ${period.id}`,
            startTime: period.start_time,
            endTime: period.end_time,
            dayOfWeek: period.day_of_week,
            teacher: period.synced_teacher ? {
              id: period.synced_teacher.id,
              name: period.synced_teacher.name,
              email: period.synced_teacher.email
            } : null
          }));
          
          console.log('Formatted periods with teacher info:', formattedPeriods.map(p => ({
            id: p.id,
            name: p.name,
            teacher: p.teacher?.name || 'No teacher assigned'
          })));
          
          setPeriods(formattedPeriods);
          
        } catch (err) {
          console.error('Error loading periods:', err);
          // Fallback to periods from reports - but load for ALL available classes
          const reportPeriods = [...new Set(reportsForSelectedDate.map(r => r.periodId))]
            .filter(Boolean)
            .map(periodId => {
              const reportWithPeriod = reportsForSelectedDate.find(r => r.periodId === periodId);
              return {
                id: periodId,
                name: reportWithPeriod?.period || `Period ${periodId}`
              };
            });
          setPeriods(reportPeriods);
        }
      } else {
        // No specific class selected - load periods from all available reports
        console.log('No specific class selected - loading periods from all reports');
        const reportPeriods = [...new Set(reportsForSelectedDate.map(r => r.periodId))]
          .filter(Boolean)
          .map(periodId => {
            const reportWithPeriod = reportsForSelectedDate.find(r => r.periodId === periodId);
            return {
              id: periodId,
              name: reportWithPeriod?.period || `Period ${periodId}`
            };
          });
        setPeriods(reportPeriods);
      }
    };

    loadPeriods();
  }, [selectedClass, selectedReportId, currentSchool?.id, getAccessTokenSilently, reportsForSelectedDate, attendanceReports]);

  // Auto-select first period that has data
  useEffect(() => {
    if (periods.length > 0 && reportsForSelectedDate.length > 0 && selectedReportId === null) {
      // Find first period that has actual data
      const availablePeriodIds = [...new Set(reportsForSelectedDate.map(r => r.periodId))];
      const periodWithDataIndex = periods.findIndex(p => availablePeriodIds.includes(p.id));
      
      console.log('Auto-select period debug:', {
        periodsCount: periods.length,
        reportsCount: reportsForSelectedDate.length,
        availablePeriodIds,
        periodWithDataIndex,
        currentSelectedPeriod: selectedPeriod
      });
      
      if (periodWithDataIndex !== -1 && periodWithDataIndex !== selectedPeriod) {
        console.log('Auto-selecting period:', periodWithDataIndex, periods[periodWithDataIndex]);
        setSelectedPeriod(periodWithDataIndex);
      }
    }
  }, [periods, reportsForSelectedDate, selectedReportId, selectedPeriod]);

  // Auto-select first report when reports list updates (but respect URL parameters)
  useEffect(() => {
    const urlReportId = searchParams.get('reportId');
    
    if (reportsForSelectedDate.length > 0) {
      // If URL specifies a report and it exists in the current list, use that
      if (urlReportId && reportsForSelectedDate.find(r => r.id === urlReportId)) {
        console.log('URL report found - selecting:', urlReportId);
        setSelectedReportId(urlReportId);
      } else {
        // Auto-select first report immediately when reports list changes
        console.log('Auto-selecting first report from updated list:', reportsForSelectedDate[0]);
        setSelectedReportId(reportsForSelectedDate[0].id);
      }
    } else {
      // Clear selection when no reports available
      console.log('No reports available - clearing selection');
      setSelectedReportId(null);
    }
    
    // Reset to first period when reports change
    setSelectedPeriod(0);
  }, [reportsForSelectedDate, searchParams]);

  // Load students for the selected report or current period
  useEffect(() => {
    const loadStudentsForReport = async () => {
      // If we have a specific report selected, load its students
      if (selectedReportId) {
        const selectedReportData = attendanceReports.find(r => r.id === selectedReportId);
        if (selectedReportData) {
          // Add a small delay before showing loading to prevent brief flashes
          const loadingTimeout = setTimeout(() => {
            setLoadingStudents(true);
          }, 150); // Only show loading if request takes longer than 150ms
          
          try {
            const token = await getAccessTokenSilently();
            const students = await AttendanceAPI.getSessionStudents(selectedReportId, currentSchool.id, token);
            clearTimeout(loadingTimeout); // Cancel loading if request completes quickly
            setReportStudents(students);
            setLoadingStudents(false);
          } catch (err) {
            console.error('Error loading students for selected report:', err);
            clearTimeout(loadingTimeout);
            setReportStudents([]);
            setLoadingStudents(false);
          }
          return;
        }
      }
      
      // If no specific report selected, try to load for current period
      if (!selectedReportId && periods.length > 0 && selectedPeriod < periods.length) {
        const currentPeriod = periods[selectedPeriod];
        const currentPeriodReport = reportsForSelectedDate.find(report => report.periodId === currentPeriod.id);
        
        if (currentPeriodReport) {
          // Add a small delay before showing loading to prevent brief flashes
          const loadingTimeout = setTimeout(() => {
            setLoadingStudents(true);
          }, 150); // Only show loading if request takes longer than 150ms
          
          try {
            const token = await getAccessTokenSilently();
            const students = await AttendanceAPI.getSessionStudents(currentPeriodReport.id, currentSchool.id, token);
            clearTimeout(loadingTimeout); // Cancel loading if request completes quickly
            setReportStudents(students);
            setLoadingStudents(false);
          } catch (err) {
            console.error('Error loading students for current period:', err);
            clearTimeout(loadingTimeout);
            setReportStudents([]);
            setLoadingStudents(false);
          }
          return;
        }
      }
      
      // Clear students if no report available
      setReportStudents([]);
      setLoadingStudents(false);
    };

    loadStudentsForReport();
  }, [selectedReportId, periods, selectedPeriod, reportsForSelectedDate, attendanceReports, currentSchool?.id, getAccessTokenSilently]);

  // Loading states - check tenant loading first, then component loading
  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading school context...</div>
        </div>
      </div>
    );
  }

  // If currentSchool is not available after tenant loading, there's an issue
  if (!currentSchool?.id) {
    console.log('❌ AttendanceReportsPage: No school found - currentSchool:', currentSchool, 'tenantLoading:', tenantLoading);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">School Not Found</div>
          <div className="text-gray-600">Unable to load school context</div>
        </div>
      </div>
    );
  }

  if (loading || tenantLoading || loadingReportsForClass) {
    console.log('🔄 AttendanceReportsPage: Showing loading spinner - loading:', loading, 'tenantLoading:', tenantLoading, 'loadingReportsForClass:', loadingReportsForClass);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Error</div>
          <div className="text-gray-600">{error}</div>
        </div>
      </div>
    );
  }

  // Helper function to check if user can modify a specific classroom
  const canModifyClassroom = (classroomId) => {
    // Normalize classroomId to integer for consistent comparison
    const normalizedClassroomId = classroomId ? parseInt(classroomId, 10) : null;
    
    if (!normalizedClassroomId || isNaN(normalizedClassroomId)) {
      return false;
    }
    
    // If user has 'all' access type (admin/staff), they can modify any classroom
    if (accessibleClassrooms.accessType === 'all') {
      return true;
    }
    
    // If user has 'restricted' access (teacher), check if classroom is in their list
    // Normalize both sides of the comparison to integers to avoid type mismatch issues
    if (accessibleClassrooms.accessType === 'restricted') {
      const normalizedAccessibleIds = accessibleClassrooms.classroomIds.map(id => parseInt(id, 10));
      return normalizedAccessibleIds.includes(normalizedClassroomId);
    }
    
    // Default to false for safety
    console.log('❌ Default deny - unknown access type');
    return false;
  };

  // Helper functions
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Handle both "HH:mm:ss" and "HH:mm" formats
    const timeParts = timeString.split(':');
    if (timeParts.length < 2) return timeString;
    
    const hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];
    
    if (hours === 0) return `12:${minutes}AM`;
    if (hours < 12) return `${hours}:${minutes}AM`;
    if (hours === 12) return `12:${minutes}PM`;
    return `${hours - 12}:${minutes}PM`;
  };

  // Helper function for timezone-aware date formatting
  const formatDateForDisplay = (dateString) => {
    if (!dateString || dateString === 'all') return null;
    
    const [year, month, day] = dateString.split('-');
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    // Use timezone-aware formatting
    const currentConfig = getCurrentTimezoneConfig();
    
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    // Apply timezone only if not LOCAL (which should use device timezone)
    if (currentConfig && currentConfig !== 'LOCAL') {
      options.timeZone = currentConfig;
    }
    
    return dateObj.toLocaleDateString('en-US', options);
  };

  // Helper function to format timestamps with timezone awareness
  const formatTimestampWithTimezone = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const currentConfig = getCurrentTimezoneConfig();
    
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    // Apply timezone only if not LOCAL (which should use device timezone)
    if (currentConfig && currentConfig !== 'LOCAL') {
      options.timeZone = currentConfig;
    }
    
    return date.toLocaleString('en-US', options);
  };

  // Helper function to get the appropriate timestamp for display (updated time if available, otherwise created time)
  const getDisplayTimestamp = (report) => {
    // Use updated time if the report was updated, otherwise use created time
    if (report.wasUpdated && report.updatedAt) {
      return report.updatedAt;
    }
    
    // Fallback to available timestamp fields
    return report.created_at || report.created_at || report.timestamp;
  };

  const getClassTitle = () => {
    if (selectedClass && selectedDate) {
      const selectedClassData = classes.find(c => c.id === Number(selectedClass));
      const className = selectedClassData ? selectedClassData.name : 'Unknown Class';
      
      if (selectedDate === 'all') {
        return `${className} Reports - All Dates`;
      }
      
      const formattedDate = formatDateForDisplay(selectedDate);
      return `${className} Reports on ${formattedDate}`;
    } else if (selectedDate) {
      if (selectedDate === 'all') {
        return 'Attendance Reports - All Dates';
      }
      
      const formattedDate = formatDateForDisplay(selectedDate);
      return `Attendance Reports for ${formattedDate}`;
    }
    return 'Attendance Reports';
  };

  const getCurrentTeachers = () => {
    console.log('🧑‍🏫 getCurrentTeachers called:', {
      periodsLength: periods.length,
      selectedPeriod,
      currentPeriodTeacher: periods[selectedPeriod]?.teacher,
      selectedClass,
      selectedDate
    });
    
    // If we have periods and a specific period is selected, show that period's teacher
    if (periods.length > 0 && selectedPeriod < periods.length) {
      const currentPeriod = periods[selectedPeriod];
      if (currentPeriod.teacher) {
        console.log('Returning period teacher:', currentPeriod.teacher.name);
        return [{
          name: currentPeriod.teacher.name,
          email: currentPeriod.teacher.email,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(currentPeriod.teacher.name)}&background=e2e8f0&color=475569`
        }];
      }
    }
    
    // Fallback: If no period selected or no teacher info, show report instructors
    if (!selectedClass || !selectedDate) return [];
    
    const reportsForSelection = attendanceReports.filter(report => {
      const reportDateSimple = report.date.includes('T') ? report.date.split('T')[0] : report.date;
      return report.classId === selectedClass && reportDateSimple === selectedDate;
    });
    
    const instructorNames = [...new Set(reportsForSelection.map(r => r.instructor))];
    
    return instructorNames.map(instructor => ({
      name: instructor,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(instructor)}&background=e2e8f0&color=475569`
    }));
  };

  const handleTakeAttendance = () => {
    // Use tenant context to navigate to the Take Attendance page with proper URL structure
    if (!currentOrganization || !currentSchool) {
      console.error('Missing required tenant context for Take Attendance navigation:', {
        currentOrganization: !!currentOrganization,
        currentSchool: !!currentSchool
      });
      return;
    }

    // Build the URL using tenant context: /org/{orgId}/school/{schoolId}/take-attendance
    const schoolUniqueId = currentSchool.unique_id || currentSchool.unique_id;
    let takeAttendanceUrl = `/org/${currentOrganization.unique_id}/school/${schoolUniqueId}/take-attendance`;
    
    // If a specific class is selected, add it as a query parameter
    if (selectedClass) {
      takeAttendanceUrl += `?class=${selectedClass}`;
    }
    
    console.log('Navigating to Take Attendance:', {
      takeAttendanceUrl,
      selectedClass,
      currentOrganization: currentOrganization.name,
      currentSchool: currentSchool.name
    });
    
    navigate(takeAttendanceUrl);
  };

  const handleReportSelection = (report) => {
    setSelectedReportId(report.id);
    
    // If the selected report is from a different class than currently selected,
    // clear the class selector to allow cross-class navigation
    if (selectedClass && report.classId !== parseInt(selectedClass)) {
      console.log('🔄 Clearing class selector for cross-class report selection:', {
        reportClass: report.classId,
        selectedClass: selectedClass,
        reportClassName: report.className
      });
      setSelectedClass('');
    }
    
    // Auto-update period tab to match the selected report
    if (periods.length > 0 && report.periodId) {
      const periodIndex = periods.findIndex(period => period.id === report.periodId);
      if (periodIndex !== -1 && periodIndex !== selectedPeriod) {
        console.log('🔄 Auto-updating selectedPeriod to match report:', periodIndex, periods[periodIndex].name);
        setSelectedPeriod(periodIndex);
      }
    }
  };

  // Handler for updating attendance status
  const handleStatusUpdate = async (recordId, newStatus) => {
    try {
      const token = await getAccessTokenSilently();
      await updateAttendanceRecord(recordId, { status: newStatus }, token);
      
      // Update local state optimistically
      setReportStudents(prev => prev.map(student => 
        student.recordId === recordId 
          ? { ...student, status: newStatus }
          : student
      ));
      
      console.log('✅ Status updated successfully for record:', recordId);
    } catch (error) {
      console.error('❌ Failed to update status:', error);
      alert('Failed to update attendance status: ' + error.message);
      throw error;
    }
  };

  // Handler for opening add student modal
  const handleOpenAddStudent = async () => {
    if (!selectedReport || !selectedReport.classId) {
      alert('Please select a report first');
      return;
    }

    setIsAddStudentModalOpen(true);
    setLoadingAvailableStudents(true);
    
    try {
      const token = await getAccessTokenSilently();
      const classroomStudents = await getClassroomStudents(selectedReport.classId, token);
      
      // Deduplicate students (same student can be in multiple periods)
      const uniqueStudentsMap = new Map();
      classroomStudents.forEach(student => {
        if (!uniqueStudentsMap.has(student.id)) {
          uniqueStudentsMap.set(student.id, student);
        }
      });
      const uniqueStudents = Array.from(uniqueStudentsMap.values());
      
      // Filter out students already in the report
      const currentStudentIds = reportStudents.map(s => s.id);
      const available = uniqueStudents.filter(s => !currentStudentIds.includes(s.id));
      
      setAvailableStudentsForAdd(available);
    } catch (error) {
      console.error('❌ Failed to load available students:', error);
      alert('Failed to load students: ' + error.message);
    } finally {
      setLoadingAvailableStudents(false);
    }
  };

  // Handler for adding a student to the report
  const handleAddStudent = async (studentId, status, studentInfo) => {
    if (!selectedReport) {
      alert('No report selected');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const result = await addStudentToSession(selectedReport.id, studentId, status, token);
      
      // Add the student to the local state
      const newStudent = {
        id: studentId,
        recordId: result.id,
        first: studentInfo.first_name,
        last: studentInfo.last_name,
        email: studentInfo.email || '',
        status: status
      };
      
      setReportStudents(prev => [...prev, newStudent]);
      setIsAddStudentModalOpen(false);
      
      console.log('✅ Student added successfully:', studentInfo.first_name, studentInfo.last_name);
    } catch (error) {
      console.error('❌ Failed to add student:', error);
      throw error;
    }
  };

  // Handler for opening remove student confirmation
  const handleOpenRemoveStudent = (student) => {
    setStudentToRemove(student);
    setIsRemoveModalOpen(true);
  };

  // Handler for confirming student removal
  const handleConfirmRemoveStudent = async () => {
    if (!studentToRemove) return;

    setIsRemovingStudent(true);
    
    try {
      const token = await getAccessTokenSilently();
      await removeStudentFromSession(studentToRemove.recordId, token);
      
      // Remove from local state
      setReportStudents(prev => prev.filter(s => s.recordId !== studentToRemove.recordId));
      setIsRemoveModalOpen(false);
      setStudentToRemove(null);
      
      console.log('✅ Student removed successfully:', studentToRemove.first, studentToRemove.last);
    } catch (error) {
      console.error('❌ Failed to remove student:', error);
      alert('Failed to remove student: ' + error.message);
    } finally {
      setIsRemovingStudent(false);
    }
  };

  // Find the selected report - prioritize selectedReportId, but ensure it matches current period
  const selectedReport = (() => {
    if (selectedReportId) {
      const reportFromId = attendanceReports.find(r => r.id === selectedReportId);
      
      // If we have a specific period selected, ensure the report matches that period
      if (periods.length > 0 && selectedPeriod < periods.length) {
        const currentPeriod = periods[selectedPeriod];
        
        if (reportFromId) {
          // Check if this report matches the current period
          const reportMatchesPeriod = currentPeriod.periodIds && currentPeriod.periodIds.length > 0
            ? currentPeriod.periodIds.includes(reportFromId.periodId)
            : reportFromId.periodId === currentPeriod.id;
            
          console.log('🔍 Period-Report Match Check:', {
            currentPeriodName: currentPeriod.name,
            currentPeriodId: currentPeriod.id,
            reportPeriodId: reportFromId.periodId,
            reportMatchesPeriod,
            selectedPeriodIndex: selectedPeriod,
            reportClass: reportFromId.className
          });
            
          // Return the report if it matches the current period OR if no class filter is applied
          if (reportMatchesPeriod || !selectedClass) {
            return reportFromId;
          }
          // If it doesn't match, fall through to period-based lookup
        }
      } else {
        // No specific period selected, return the report
        return reportFromId;
      }
    }
    
    // Fallback: look for report matching current period
    if (periods.length > 0 && selectedPeriod < periods.length) {
      const currentPeriod = periods[selectedPeriod];
      // When looking for a report for the current period, consider class context
      const relevantReports = selectedClass 
        ? reportsForSelectedDate.filter(report => report.classId === parseInt(selectedClass))
        : reportsForSelectedDate;
      
      const periodReport = relevantReports.find(report => {
        if (currentPeriod.periodIds && currentPeriod.periodIds.length > 0) {
          return currentPeriod.periodIds.includes(report.periodId);
        } else {
          return report.periodId === currentPeriod.id;
        }
      });
      
      // Update selectedReportId to match the period report found
      if (periodReport && selectedReportId !== periodReport.id) {
        console.log('🔄 Auto-updating selectedReportId to match period:', periodReport.id);
        setSelectedReportId(periodReport.id);
      }
      
      return periodReport;
    }
    
    return null;
  })();

  console.log('🎯 AttendanceReportsPage: About to render main content - classes:', classes.length, 'reports:', attendanceReports.length, 'periods:', periods.length);

  return (
    <>
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className=" mx-auto px-0 py-2">    

      {/* Header with Class, Date, and Teacher selector */}
      <AttendanceClassDateSelector
        classes={classes}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        teachers={getCurrentTeachers()}
      />

      <div className="grid grid-cols-12 gap-8 mt-8">
        {/* Left Sidebar - Reports List */}
        <div className="col-span-3">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">
            {getClassTitle()}
          </h1>
          
          <div className="w-full flex flex-col space-y-3 overflow-y-auto">
            {/* Reports summary */}
            {totalReports > 0 && (
              <div className="text-sm text-gray-600 px-2">
                Showing {paginatedReports.length} of {totalReports} reports
                {totalReports > reportsPerPage && ` (Page ${currentPage + 1} of ${totalPages})`}
              </div>
            )}
            
            {/* Pagination Navigation */}
            {totalReports > reportsPerPage && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200 mb-2">
                <button
                  onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Previous</span>
                </button>
                
                <div className="text-sm text-gray-600">
                  Page {currentPage + 1} of {totalPages}
                </div>
                
                <button
                  onClick={() => handlePageChange(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
            
            {loadingReportsForClass ? (
              <div className="text-gray-500 py-4 flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading reports...
              </div>
            ) : reportsForSelectedDate.length === 0 ? (
              <div className="text-gray-500 py-4">
                No attendance reports found.
              </div>
            ) : (
              paginatedReports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => handleReportSelection(report)}
                  className={`flex justify-between items-start p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedReportId === report.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{report.instructor}</div>
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">
                        {report.wasUpdated ? 'Updated • ' : 'Created • '} 
                      </span>
                      {formatTimestampWithTimezone(getDisplayTimestamp(report))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 ml-4 text-right">
                    <div className="font-medium text-gray-700">{report.className}</div>
                    <div>{report.period}</div>
                  </div>
                </div>
              ))
            )}
            
            {/* Take Attendance Button */}
            <div className="mt-6">
              <button 
                onClick={handleTakeAttendance}
                disabled={tenantLoading || !currentOrganization || !currentSchool}
                className={`w-full px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors ${
                  tenantLoading || !currentOrganization || !currentSchool
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                {tenantLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <span>Take Attendance</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Content - Student Table */}
        <div className="col-span-9">
          {periods.length > 0 && reportsForSelectedDate.length > 0 && (selectedClass || selectedReportId) ? (
            <div>
              {/* Period Tabs with Horizontal Scroll */}
              <div className="border-b border-gray-200 mb-6">
                <div className="relative">
                  {/* Left scroll arrow */}
                  {periodsNeedScrolling && (
                    <button
                      onClick={() => scrollPeriods('left')}
                      disabled={!canScrollLeft}
                      className={`absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-white to-transparent z-10 flex items-center justify-center transition-opacity ${
                        canScrollLeft ? 'hover:from-gray-50' : 'opacity-30 cursor-not-allowed'
                      }`}
                      style={{ marginBottom: '1px' }}
                    >
                      <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Scrollable periods container */}
                  <nav 
                    ref={periodsContainerRef}
                    className={`-mb-px flex space-x-8 overflow-x-auto scrollbar-hide ${periodsNeedScrolling ? 'px-10' : ''}`}
                    style={{ 
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitScrollbar: 'none'
                    }}
                    onScroll={(e) => setPeriodScrollPosition(e.target.scrollLeft)}
                  >
                  {periods.map((period, index) => {
                    // Check if there's a report for this period (more reliable than student count)
                    const periodReport = reportsForSelectedDate.find(report => report.periodId === period.id);
                    const isDisabled = !periodReport;
                    
                    return (
                      <div key={period.id} className="relative group">
                        <button
                          onClick={() => {
                            if (isDisabled) return; // Don't allow clicks on disabled tabs
                            
                            setSelectedPeriod(index);
                            
                            // Find and select the report for this period
                            if (periodReport && selectedReportId !== periodReport.id) {
                              console.log('🔄 Period tab clicked - updating selectedReportId:', periodReport.id, periodReport.className, periodReport.period);
                              
                              // Check if the period report is on the current page
                              const reportOnCurrentPage = paginatedReports.find(r => r.id === periodReport.id);
                              
                              if (reportOnCurrentPage) {
                                // Report is on current page - just switch to it without navigation
                                console.log('📄 Period report found on current page - switching without navigation');
                                setSelectedReportId(periodReport.id);
                              } else {
                                // Report is on different page - navigate to it
                                const reportIndex = reportsForSelectedDate.findIndex(r => r.id === periodReport.id);
                                const targetPage = Math.floor(reportIndex / reportsPerPage);
                                console.log('📄 Period report on different page - navigating from page', currentPage, 'to page', targetPage);
                                setSelectedReportId(periodReport.id);
                                handlePageChange(targetPage);
                              }
                            }
                          }}
                          disabled={isDisabled}
                          className={`py-2 px-3 border-b-2 font-medium text-sm transition-all whitespace-nowrap flex-shrink-0 ${
                            isDisabled
                              ? 'border-transparent text-gray-300 cursor-not-allowed'
                              : selectedPeriod === index
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {period.name}
                          {period.startTime && period.endTime && (
                            <div className="text-xs mt-1">
                              <span className={isDisabled ? "text-gray-300" : "text-gray-400"}>
                                {displayPeriodTime(formatTime(period.startTime), formatTime(period.endTime))}
                              </span>
                            </div>
                          )}
                        </button>
                        
                        {/* Tooltip for disabled periods */}
                        {isDisabled && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                            No reports found
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </nav>
                  
                  {/* Right scroll arrow */}
                  {periodsNeedScrolling && (
                    <button
                      onClick={() => scrollPeriods('right')}
                      disabled={!canScrollRight}
                      className={`absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-center transition-opacity ${
                        canScrollRight ? 'hover:from-gray-50' : 'opacity-30 cursor-not-allowed'
                      }`}
                      style={{ marginBottom: '1px' }}
                    >
                      <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Content Area */}
              {(() => {
                console.log('🎯 Content Area Logic:', {
                  selectedReport: selectedReport ? {
                    id: selectedReport.id,
                    className: selectedReport.className,
                    period: selectedReport.period,
                    periodId: selectedReport.periodId
                  } : null,
                  selectedPeriod,
                  currentPeriodName: periods[selectedPeriod]?.name,
                  currentPeriodId: periods[selectedPeriod]?.id
                });
                
                return selectedReport;
              })() ? (
                /* Student Table - when we have a valid report */
                <div className="bg-white rounded-lg shadow overflow-visible">
                  {/* Search Bar and Stats */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      {/* Student Count Info with Tooltip */}
                      {filteredStudents.length > 0 && (
                        <div className="relative group text-sm text-gray-600 flex-shrink-0">
                          <div className="cursor-help inline-flex items-center">
                            {filteredStudents.length > studentsPerPage ? (
                              <span>
                                Showing {currentStudentPage * studentsPerPage + 1}-{Math.min((currentStudentPage + 1) * studentsPerPage, filteredStudents.length)} of {filteredStudents.length} students
                              </span>
                            ) : (
                              <span>{filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}</span>
                            )}
                            <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          
                          {/* Tooltip */}
                          {reportStudents.length > 0 && (
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-[100] pointer-events-none">
                              <div className="bg-slate-800 text-white text-xs rounded-lg shadow-xl p-3 w-48">
                                <div className="font-semibold mb-2 text-slate-100 border-b border-slate-600 pb-1">Attendance Summary</div>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                                      <span className="text-slate-200">Present:</span>
                                    </div>
                                    <span className="font-semibold text-white ml-2">{reportStudents.filter(s => s.status === 'Present').length}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="w-2.5 h-2.5 bg-red-500 rounded-full mr-2 flex-shrink-0"></div>
                                      <span className="text-slate-200">Absent:</span>
                                    </div>
                                    <span className="font-semibold text-white ml-2">{reportStudents.filter(s => s.status === 'Absent').length}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="w-2.5 h-2.5 bg-yellow-500 rounded-full mr-2 flex-shrink-0"></div>
                                      <span className="text-slate-200">Late:</span>
                                    </div>
                                    <span className="font-semibold text-white ml-2">{reportStudents.filter(s => s.status === 'Late').length}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="w-2.5 h-2.5 bg-blue-500 rounded-full mr-2 flex-shrink-0"></div>
                                      <span className="text-slate-200">Excused:</span>
                                    </div>
                                    <span className="font-semibold text-white ml-2">{reportStudents.filter(s => s.status === 'Excused').length}</span>
                                  </div>
                                </div>
                                {/* Tooltip Arrow - positioned to align with the info icon */}
                                <div className="absolute -top-1 left-[5.5rem] w-2 h-2 bg-slate-800 transform rotate-45"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="relative flex-1">
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-lg pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search Student..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      
                      {/* Add Student Button */}
                      <button
                        onClick={handleOpenAddStudent}
                        disabled={!canModifyClassroom(selectedReport?.classId)}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                          canModifyClassroom(selectedReport?.classId)
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        title={!canModifyClassroom(selectedReport?.classId) ? 'No access to modify this classroom' : 'Add student to report'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Add Student</span>
                      </button>
                    </div>
                  </div>

                  {/* Table Header */}
                  <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                    <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                      <div className="col-span-3">First</div>
                      <div className="col-span-3">Last</div>
                      <div className="col-span-3">Email</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-1">Actions</div>
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="divide-y divide-gray-200 overflow-visible">
                    {loadingStudents ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : filteredStudents.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        No students found.
                      </div>
                    ) : (
                      (() => {
                        // Check access once for the entire report
                        const reportClassId = selectedReport?.classId;
                        const hasAccess = canModifyClassroom(reportClassId);
                        
                        return paginatedStudents.map((student) => {
                          return (
                          <div key={student.id} className="px-6 py-4 hover:bg-gray-50">
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-3 font-medium text-gray-900">
                                {student.first}
                              </div>
                              <div className="col-span-3 text-gray-900">
                                {student.last}
                              </div>
                              <div className="col-span-3 text-gray-600">
                                {student.email || '-'}
                              </div>
                              <div className="col-span-2">
                                <AttendanceStatusEditor
                                  currentStatus={student.status.toUpperCase()}
                                  onUpdate={(newStatus) => handleStatusUpdate(student.recordId, newStatus)}
                                  disabled={!hasAccess}
                                />
                              </div>
                              <div className="col-span-1 flex justify-center">
                                {hasAccess ? (
                                  <button
                                    onClick={() => handleOpenRemoveStudent(student)}
                                    className="text-red-600 hover:text-red-800 transition-colors"
                                    title="Remove student"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                ) : (
                                  <div className="flex items-center justify-center text-gray-400 cursor-not-allowed" title="No access to modify this classroom">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                      })()
                    )}
                  </div>
                  
                  {/* Student Pagination Controls */}
                  {filteredStudents.length > studentsPerPage && (
                    <div className="flex items-center justify-between mt-4 py-4 px-4">
                      <div className="text-sm text-slate-700 font-medium">
                        {filteredStudents.length === 0
                          ? 'Showing 0 of 0 students'
                          : `Showing ${currentStudentPage * studentsPerPage + 1}-${Math.min((currentStudentPage + 1) * studentsPerPage, filteredStudents.length)} of ${filteredStudents.length} students`}
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setCurrentStudentPage(Math.max(0, currentStudentPage - 1))}
                          disabled={currentStudentPage === 0}
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${currentStudentPage === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                        >
                          Previous
                        </button>
                        <div className="flex items-center gap-1">
                          {[...Array(totalStudentPages)].map((_, index) => (
                            <button
                              key={index + 1}
                              onClick={() => setCurrentStudentPage(index)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium ${currentStudentPage === index ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            >
                              {index + 1}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setCurrentStudentPage(Math.min(totalStudentPages - 1, currentStudentPage + 1))}
                          disabled={currentStudentPage >= totalStudentPages - 1}
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${currentStudentPage >= totalStudentPages - 1 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* No report available - determine why */
                (() => {
                  // Check if a specific period is selected but has no report
                  if (periods.length > 0 && selectedPeriod < periods.length) {
                    const currentPeriod = periods[selectedPeriod];
                    
                    // Determine which reports to check based on context
                    let relevantReports = reportsForSelectedDate;
                    
                    // If we have a selected report, check only for reports from that class
                    const selectedReportForClass = selectedReportId 
                      ? attendanceReports.find(r => r.id === selectedReportId)
                      : null;
                      
                    if (selectedReportForClass) {
                      relevantReports = reportsForSelectedDate.filter(report => 
                        report.classId === selectedReportForClass.classId
                      );
                    } else if (selectedClass) {
                      // If no specific report selected but class is selected, filter by class
                      relevantReports = reportsForSelectedDate.filter(report => 
                        report.classId === selectedClass
                      );
                    }
                    
                    const hasReportForPeriod = relevantReports.some(report => {
                      if (currentPeriod.periodIds && currentPeriod.periodIds.length > 0) {
                        return currentPeriod.periodIds.includes(report.periodId);
                      } else {
                        return report.periodId === currentPeriod.id;
                      }
                    });
                    
                    if (!hasReportForPeriod) {
                      // Period selected but no report found for this period
                      return (
                        <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-dashed border-2 border-gray-200">
                          <div className="text-center max-w-sm">
                            <svg className="mx-auto h-16 w-16 text-orange-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h3 className="mt-4 text-lg font-medium text-gray-900">Report Not Found</h3>
                            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                              No attendance report found for {currentPeriod.name} on {(() => {
                                if (!selectedDate) return 'the selected date';
                                return formatDateForDisplay(selectedDate);
                              })()}{selectedReportForClass ? ` for ${selectedReportForClass.className}` : ''}.
                            </p>
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  // Default case - no period selected or no periods available
                  return (
                    <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-dashed border-2 border-gray-200">
                      <div className="text-center max-w-sm">
                        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-4 text-lg font-medium text-gray-900">No Report Selected</h3>
                        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                          {!selectedClass && !selectedDate ? 
                            "Select a class and date to view attendance reports." :
                            "Choose a report from the list on the left to view detailed attendance information."
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-dashed border-2 border-gray-200">
              <div className="text-center max-w-sm">
                <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Reports Available</h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  {!selectedClass && !selectedDate ? 
                    "Select a class and date to view attendance reports." :
                    "No attendance reports found for the selected filters."
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Add Student Modal */}
    <AddStudentToReportModal
      isOpen={isAddStudentModalOpen}
      onClose={() => setIsAddStudentModalOpen(false)}
      onAddStudent={handleAddStudent}
      availableStudents={availableStudentsForAdd}
      isLoading={loadingAvailableStudents}
    />

    {/* Remove Student Confirmation Modal */}
    <RemoveStudentModal
      isOpen={isRemoveModalOpen}
      onClose={() => {
        setIsRemoveModalOpen(false);
        setStudentToRemove(null);
      }}
      onConfirm={handleConfirmRemoveStudent}
      studentName={studentToRemove ? `${studentToRemove.first} ${studentToRemove.last}` : ''}
      isRemoving={isRemovingStudent}
    />
    </>
  );
}